import type { FC } from "react";
import { useCallback, useState } from "react";

import XRegExp from "xregexp";

import { i18nFilter } from "app/filters/i18nFilter";
import type { ISegmentPageUrlListProps, ISegmentUrl } from "app/pages/segments/redesigned-wizard/segment-wizard/types";
import {
    escapeRegexString,
    KEYWORDS_DELIMITER_REGEX_PATTERN_END,
    KEYWORDS_DELIMITER_REGEX_PATTERN_START,
} from "app/services/segments/segmentsProcessor/segmentsKeywordsVariables";
import { highlightUrl } from "app/services/segments/SegmentUrlHighlights";

import { SegmentUrlListItem } from "./SegmentUrlListItem";
import { AutocompleteStyled, Separator, SearchWrapper, UrlsScrollArea } from "./SegmentUrlListStyles";

export const SegmentUrlList: FC<ISegmentPageUrlListProps> = (props) => {
    const { selectedSite, wordHightlights, exactPhraseHighlights, urls, shouldShowFilter, isFilterComponent } = props;
    const [filterTerm, setFilterTerm] = useState(null);

    const scrollBarStyles = isFilterComponent
        ? {
              verticalScrollbarStyle: { borderRadius: 8, width: 5 },
              verticalContainerStyle: { width: 5 },
              minScrollSize: 48,
          }
        : { verticalScrollbarStyle: { borderRadius: 5 } };

    const onSearch = useCallback((e) => {
        setFilterTerm(e.target.value);
    }, []);

    /**
     * Renders the list of Urls that should be displayed, after filtering
     * them according to the selected filterTerm, if such exists
     */
    const renderUrlListItems = (takeAmount = 100) => {
        const filteredUrls = getUrls(filterTerm);

        // Check if a filter term was selected in the URLs list search
        // in case such term was selected by the user - we should highlight the words
        // according to the search filter. otherwise - we want to highlight words according
        // to chosen word predictions.
        const wordsToHighlight = filterTerm ? [filterTerm] : wordHightlights;

        const exactToHighlight = filterTerm ? [] : exactPhraseHighlights;

        // gather distinct urls for the segment url list preview
        const filteredUrlsMap = new Map<string, { highlighted: any; urls: ISegmentUrl[] }>();
        for (let i = 0; i < filteredUrls.length && filteredUrlsMap.size < takeAmount; ++i) {
            const urlSegment = filteredUrls[i];
            const [urlShort, urlHighlights] = highlightUrl(
                urlSegment.url,
                selectedSite,
                wordsToHighlight,
                exactToHighlight,
            );
            if (!filteredUrlsMap.has(urlShort)) {
                filteredUrlsMap.set(urlShort, {
                    highlighted: urlHighlights,
                    urls: [],
                });
            }
            const urlGroup = filteredUrlsMap.get(urlShort);
            urlGroup.urls.push(urlSegment);
        }

        return Array.from(filteredUrlsMap.entries()).map(([urlShort, { highlighted, urls }]) => {
            return (
                <SegmentUrlListItem
                    key={urlShort}
                    urlHighlighted={highlighted}
                />
            );
        });
    };

    /**
     * Returns the list of urls, and filters out any urls that do not contain
     * the provided filterTerm (if such was provided)
     */
    const getUrls = (filterByTerm?: string, searchByLetters = true): ISegmentUrl[] => {
        if (!filterByTerm) {
            return urls;
        }
        const filterRegex = XRegExp(
            `(?:${KEYWORDS_DELIMITER_REGEX_PATTERN_START})(${escapeRegexString(
                filterByTerm,
            )})(?=${KEYWORDS_DELIMITER_REGEX_PATTERN_END})`,
            "ui",
        );

        if (searchByLetters) {
            return urls.filter((url) => url.url.includes(filterByTerm));
        }
        return urls.filter((url) => url.url.match(filterRegex));
    };

    return (
        <>
            {shouldShowFilter ? (
                <SearchWrapper>
                    <AutocompleteStyled
                        placeholder={i18nFilter()("workspaces.segment.wizard.url.result.search")}
                        onKeyUp={onSearch}
                        hideBorder={true}
                        isFocused={false}
                    />
                    <Separator />
                </SearchWrapper>
            ) : null}

            <UrlsScrollArea {...scrollBarStyles}>{renderUrlListItems(100)}</UrlsScrollArea>
        </>
    );
};
